"use client";

import React, { createContext, useState, useEffect, useCallback, useMemo } from "react";
import type { FeatureFlags, FeatureFlagContextType } from "./types";
import { DEFAULT_FEATURE_FLAGS } from "./defaults";

export const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

export function FeatureFlagProvider({ children }: { children: React.ReactNode }) {
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FEATURE_FLAGS);

  useEffect(() => {
    const savedFlags = localStorage.getItem("feature-flags");
    if (savedFlags) {
      try {
        const parsedFlags = JSON.parse(savedFlags);
        setFlags({ ...DEFAULT_FEATURE_FLAGS, ...parsedFlags });
      } catch (error) {
        console.error("Error parsing saved feature flags:", error);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("feature-flags", JSON.stringify(flags));
  }, [flags]);

  const updateFlag = useCallback((flagName: keyof FeatureFlags, value: boolean) => {
    setFlags((prev) => ({ ...prev, [flagName]: value }));
  }, []);

  const updateFlags = useCallback((updates: Partial<FeatureFlags>) => {
    setFlags((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetFlags = useCallback(() => {
    setFlags(DEFAULT_FEATURE_FLAGS);
    localStorage.removeItem("feature-flags");
  }, []);

  const isEnabled = useCallback(
    (flagName: keyof FeatureFlags) => flags[flagName] === true,
    [flags]
  );

  const isDisabled = useCallback(
    (flagName: keyof FeatureFlags) => flags[flagName] === false,
    [flags]
  );

  const value: FeatureFlagContextType = useMemo(
    () => ({
      flags,
      updateFlag,
      updateFlags,
      resetFlags,
      isEnabled,
      isDisabled,
      isFeatureEnabled: isEnabled,
    }),
    [flags, updateFlag, updateFlags, resetFlags, isEnabled, isDisabled]
  );

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
}
